import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowIcon, Button, ChoiceList } from '@cmsgov/ds-medicare-gov';
import { trackFiltersApplied } from '@shared/analytics';
import type { Facets, FacetTerm } from '../types';
import type { Lang, FacetKey } from '../constants';
import { FACETS } from '../constants';
import { t } from '../i18n';
import './FilterSet.css';

interface FilterSetProps {
  lang: Lang;
  facets: Facets | null;
  activeFilters: Record<FacetKey, number[]>;
  disabled: boolean;
  totalResults: number;
  onApply: (allFilters: Record<FacetKey, number[]>) => void;
}

// Defines the display order of filter categories left-to-right.
const FACET_ORDER: FacetKey[] = [
  FACETS.PRICE_TYPE,
  FACETS.PLATFORMS,
  FACETS.CONDITION,
  FACETS.FEATURES,
];

// ---------------------------------------------------------------------------
// FilterPanel — a single filter button + dropdown panel
//
// Stateless presentational component. FilterSet owns all state and passes it
// down via props. Each FilterPanel renders one filter category (e.g. "Price").
// ---------------------------------------------------------------------------

interface FilterPanelProps {
  facetKey: FacetKey;
  label: string;
  terms: FacetTerm[];
  isOpen: boolean;
  activeCount: number;         // Number of applied (committed) selections for this facet.
  pendingSelections: number[]; // TIDs checked in the panel but not yet applied.
  disableInteraction: boolean; // True before data loads or on fatal error.
  strings: ReturnType<typeof t>;
  onToggle: () => void;
  // Receives the tid of the toggled term.
  onChoiceChange: (tid: number) => void;
  onApply: () => void;
  onClear: () => void;
}

function FilterPanel({
  facetKey,
  label,
  terms,
  isOpen,
  activeCount,
  pendingSelections,
  disableInteraction,
  strings,
  onToggle,
  onChoiceChange,
  onApply,
  onClear,
}: FilterPanelProps) {
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // When no terms are relevant (e.g. a keyword search returned 0 results),
  // all checkboxes are disabled and Apply/Clear should also be disabled.
  const allDisabled = terms.length > 0 && terms.every((term) => !term.relevant);
  const pendingCount = pendingSelections.length;

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Node && !filterPanelRef.current?.contains(target)) {
        onToggle();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen, onToggle]);

  return (
    <div
      ref={filterPanelRef}
      className={`app-library-search__filter-set-filter app-library-search__filter-set-filter--${facetKey}`}
    >
      {/* Toggle button shows the count of applied selections. */}
      <Button
        variation="ghost"
        onClick={onToggle}
        aria-expanded={isOpen}
        disabled={disableInteraction}
        className="app-library-search__filter-set-filter-toggle"
        id={`${facetKey}GroupTrigger`}
        aria-controls={`${facetKey}Group`}
      >
        {label}
        {/* Show applied count badge, e.g. "Health Condition (2)". */}
        {activeCount > 0 && ` (${activeCount})`}
        {isOpen && <ArrowIcon direction={"up"}/>}
        {!isOpen && <ArrowIcon direction={"down"}/>}
      </Button>

      {/* Dropdown panel — only mounted when open. */}
      {isOpen && (
        <div
          className="ds-c-dropdown__menu-container app-library-search__filter-set-filter-panel"
          role="group"
          aria-label={label}
          aria-labelledby={`${facetKey}GroupTrigger`}
          id={`${facetKey}Group`}
        >
          {/* Checkbox list for all terms in this facet category.
              - value is the term's primary tid (a single integer as a string).
              - checked reflects pending (not yet applied) state.
              - disabled means the term has no results under the current search/filters.
              - data-hte-tids is present on app_key_features terms that reference one
                or more app_hte_categories terms via field_related_hte_category. The
                backend will automatically expand those tids when filtering. */}
          <ChoiceList
            type="checkbox"
            name={facetKey}
            label={label}
            labelClassName="ds-u-visibility--screen-reader"
            choices={terms.map((term) => ({
              label: term.name,
              value: String(term.tid),
              checked: pendingSelections.includes(term.tid),
              disabled: !term.relevant,
              ...(term.related_hte_category?.length
                ? { 'data-hte-tids': term.related_hte_category.map((related) => related.tid).join(',') }
                : {}),
            }))}
            onChange={(event) => onChoiceChange(Number(event.currentTarget.value))}
            className="app-library-search__filter-set-filter-panel-choices"
          />

          <div className="app-library-search__filter-set-filter-panel-actions">
            {/* Clear resets only this facet's pending selections without submitting. */}
            <Button
              type="button"
              size="small"
              onClick={onClear}
              disabled={pendingCount === 0 || allDisabled}
              className="app-library-search__filter-set-filter-panel-actions-clear"
              aria-disabled={pendingCount === 0 || allDisabled}
            >
              {strings.clear}
            </Button>
            {/* Apply commits ALL pending selections across every facet and closes panels. */}
            <Button
              type="button"
              variation="solid"
              size="small"
              onClick={onApply}
              disabled={allDisabled}
              className="ds-c-button--alternate"
            >
              {strings.apply}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterSet — manages open-panel state and pending selections for all facets
//
// "Pending" means checked but not yet applied. Selections persist across
// panel open/close cycles and only sync to activeFilters when they change
// externally (chip removal, full reset from the parent).
// ---------------------------------------------------------------------------

export function FilterSet({
  lang,
  facets,
  activeFilters,
  disabled,
  totalResults,
  onApply,
}: FilterSetProps) {
  // Tracks which filter panels are currently open (multiple can be open at once).
  const [openPanels, setOpenPanels] = useState<Set<FacetKey>>(new Set());

  // Pending selections are the checkbox state inside each panel before Apply is clicked.
  // Initialized from activeFilters so any URL-restored filters are pre-checked on first render.
  const [pendingSelections, setPendingSelections] = useState<Record<FacetKey, number[]>>(
    { ...activeFilters }
  );
  const strings = t(lang);

  // Sync pending selections when activeFilters change externally (chip removal,
  // reset). Uses setState-during-render instead of useEffect to avoid the
  // "setState inside effect" lint warning and cascading render cycle.
  // React detects this pattern, bails out of the current render, and re-renders
  // once to stabilize — no performance cost and no stale state.
  const [prevFiltersJSON, setPrevFiltersJSON] = useState(
    () => JSON.stringify(activeFilters)
  );
  const currentFiltersJSON = JSON.stringify(activeFilters);
  if (prevFiltersJSON !== currentFiltersJSON) {
    setPrevFiltersJSON(currentFiltersJSON);
    setPendingSelections({ ...activeFilters });
  }

  // Global ESC key listener — closes all open panels.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openPanels.size > 0) {
        setOpenPanels(new Set());
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openPanels]);

  // Toggle a single panel open or closed without affecting others.
  const handleToggle = useCallback((facetKey: FacetKey) => {
    setOpenPanels((prev) => {
      // If already open, close it. If closed, open only this panel.
      if (prev.has(facetKey)) {
        const next = new Set(prev);
        next.delete(facetKey);
        return next;
      }
      return new Set([facetKey]);
    });
  }, []);

  // Toggle a single tid in the pending selections for a given facet.
  // Adding/removing pending tids does not trigger a search — that happens
  // only when the user clicks Apply.
  const handleChoiceChange = useCallback((facetKey: FacetKey, tid: number) => {
    setPendingSelections((prev) => {
      const current = prev[facetKey] || [];
      if (current.includes(tid)) {
        return { ...prev, [facetKey]: current.filter((id) => id !== tid) };
      }
      return { ...prev, [facetKey]: [...current, tid] };
    });
  }, []);

  // Commit all pending selections across every facet, close all panels,
  // and fire the analytics event for whichever categories were used.
  const handleApply = useCallback(() => {
    onApply(pendingSelections);
    setOpenPanels(new Set());

    const usedCategories = FACET_ORDER
      .filter((key) => (pendingSelections[key] || []).length > 0)
      .map((key) => strings.facetLabels[key])
      .join(', ');

    if (usedCategories) {
      trackFiltersApplied({
        filterCategoriesUsed: usedCategories,
        totalResults,
        countBeforeFilter: totalResults,
        countAfterFilter: 0,
      });
    }
  }, [onApply, pendingSelections, strings.facetLabels, totalResults]);

  // Clear pending selections for one facet only — does NOT trigger a search.
  const handleClear = useCallback((facetKey: FacetKey) => {
    setPendingSelections((prev) => ({ ...prev, [facetKey]: [] }));
  }, []);

  // Disable all toggle buttons before the first API response arrives or on fatal error.
  const disableInteraction = !facets || disabled;

  return (
    <div className="app-library-search__filter-set ds-u-display--none ds-u-lg-display--block">
      <div className="app-library-search__filter-set-container">
        <span className="app-library-search__filter-set-label">
          {strings.filterBy}
        </span>
        <div className="app-library-search__filter-set-filters">
          {/* Render one FilterPanel per facet in the defined display order. */}
          {FACET_ORDER.map((facetKey) => (
            <FilterPanel
              key={facetKey}
              facetKey={facetKey}
              label={strings.facetLabels[facetKey]}
              terms={facets?.[facetKey] || []}
              isOpen={openPanels.has(facetKey)}
              activeCount={(facets?.[facetKey] || []).filter((t) => (activeFilters[facetKey] || []).includes(t.tid)).length}
              pendingSelections={pendingSelections[facetKey] || []}
              disableInteraction={disableInteraction}
              strings={strings}
              onToggle={() => handleToggle(facetKey)}
              onChoiceChange={(tid) => handleChoiceChange(facetKey, tid)}
              onApply={handleApply}
              onClear={() => handleClear(facetKey)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
